import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SeoHead from '../components/SeoHead'
import GalleryLightbox from '../components/GalleryLightbox'
import { getCmsPage, getCmsGallery, getCmsCollection } from '../lib/cms'
import { getOptimizedImageUrl } from '../lib/utils'

const getServiceIcon = (title = '') => {
  const t = title.toLowerCase()
  if (t.includes('udmåling') || t.includes('clio')) return 'fa-light fa-chart-waveform'
  if (t.includes('udvikling') || t.includes('højtaler')) return 'fa-light fa-volume-high'
  if (t.includes('hypex') || t.includes('elektronik')) return 'fa-light fa-microchip'
  return 'fa-light fa-sliders-up'
}

export default function Home() {
  const [pageData, setPageData] = useState(null)
  const [galleryData, setGalleryData] = useState({ gallery: null, items: [] })
  const [hifiItems, setHifiItems] = useState([])

  useEffect(() => {
    let isMounted = true
    Promise.all([
      getCmsPage('forside'),
      getCmsGallery('hifi-setup'),
      getCmsCollection('hifi_items')
    ]).then(([page, gallery, items]) => {
      if (isMounted) {
        setPageData(page)
        setGalleryData(gallery)
        setHifiItems(items || [])
      }
    })
    return () => {
      isMounted = false
    }
  }, [])

  const heroImage = 'https://assets.auxodev.dk/assets/d6e713fb-6411-4075-ab6a-5936b7a50d5a/image.png'
  const detailImage = 'https://assets.auxodev.dk/assets/d6e713fb-6411-4075-ab6a-5936b7a50d5a/2dee4a-10d0de04f8d84061b64ec76601f0ef3c-mv2.jpg'
  const listeningPosImage = 'https://assets.auxodev.dk/assets/d6e713fb-6411-4075-ab6a-5936b7a50d5a/2dee4a-75a790a4e93b4a67a6981ddeae1ed7f4-mv2.jpg'
  const ampImage = 'https://assets.auxodev.dk/assets/d6e713fb-6411-4075-ab6a-5936b7a50d5a/2dee4a-d78699cbcd994ac0866c1032fa7a7f83-mv2-d-4608-3072-s-4-2.jpg'

  return (
    <div className="bg-[#121214] text-[#F5F3EF]">
      <SeoHead
        title={pageData?.seo_title || "JARCon ApS | JARC Audio – High-End HiFi & Højttalerbyg i Sydals"}
        description={
          pageData?.meta_description ||
          "Oplev musik med nerve hos JARCon ApS. Specialister i high-end HiFi, håndbyggede højttalere og akustisk rådgivning. Book en demo i vores lytterum i Sydals."
        }
        ogImage={heroImage}
        canonicalUrl="https://www.jarcon.dk/"
      />

      {/* HERO SECTION */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden border-b border-white/10">
        {/* Background photo with subtle dark gradient overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/d6e713fb-6411-4075-ab6a-5936b7a50d5a/image.png"
            alt="JARC Audio lytterum"
            className="h-full w-full object-cover object-center brightness-[0.45]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#121214] via-[#121214]/50 to-transparent" />
          <div className="absolute inset-0 bg-radial-at-center from-transparent via-[#121214]/40 to-[#121214]/80" />
        </div>

        {/* Hero content */}
        <div className="relative z-10 mx-auto max-w-5xl px-4 py-24 sm:px-6 lg:px-8 text-center flex flex-col items-center">
          <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight text-[#F5F3EF] leading-[1.08] font-normal">
            Musik med nerve
          </h1>

          <p className="mt-6 max-w-2xl font-serif text-xl sm:text-2xl md:text-3xl text-[#E5E2DB] font-light italic">
            “Kom forbi til et nyt lyt og en kop kaffe”
          </p>

          <p className="mt-6 max-w-xl text-sm sm:text-base text-[#A8A49C] leading-relaxed">
            Målet er at skabe en højtaler, som kan gengive al slags musik med nerve og nærvær, hvor lytteren glemmer at man lytter til HiFi, men føler sig omslugt at musikken.
          </p>

          {/* Call to action buttons */}
          <div className="mt-10 flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
            <Link
              to="/hifi"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 rounded-sm bg-[#C87D46] px-8 py-4 text-sm font-medium text-white shadow-xl shadow-[#C87D46]/25 hover:bg-[#D98E58] transition-all"
            >
              <span>Udforsk HiFi universet</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>

            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 rounded-sm border border-white/20 bg-[#18181C]/80 px-8 py-4 text-sm font-medium text-[#F5F3EF] hover:bg-white/10 hover:border-[#C87D46]/50 transition-all backdrop-blur-sm"
            >
              <i className="fa-solid fa-mug-hot text-xs text-[#C87D46]" aria-hidden="true" />
              <span>Aftal et lyt & kaffe</span>
            </Link>
          </div>
        </div>
      </section>

      {/* EDITORIAL INTRODUCTION SECTION */}
      <section className="py-20 sm:py-28 border-b border-white/10 relative">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left text column */}
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-block text-xs uppercase tracking-widest text-[#C87D46] font-medium">
                Om JARC Audio
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#F5F3EF] leading-tight">
                HiFi bygget med passion, præcision og ægte musikalitet
              </h2>
              <div className="space-y-4 text-sm sm:text-base text-[#A8A49C] leading-relaxed">
                <p>
                  HiFi og DIY HiFi har været en passion og hobby for mig i mange år. Jeg har lavet pladespillere, kabler og tilbehør, men primært højtalerbyg, har min interesse.
                </p>
                <p>
                  Det er blevet til mange spændende højtaler konstruktioner gennem årene. For mig handler et godt anlæg ikke om tekniske specifikationer alene, men om den følelse, musikken vækker. Det får musikken til at &ldquo;gå under huden&rdquo; på lytteren, så man nærmest glemmer at det er et HiFi anlæg man lytter til.
                </p>
                <p>
                  Hos JARCon ApS kombineres mange års praktisk byggeerfaring med moderne udmåling og udvalgt forstærkerteknologi.
                </p>
              </div>

              <div className="pt-4 flex flex-wrap gap-4">
                <Link
                  to="/hifi"
                  className="inline-flex items-center gap-2.5 rounded-sm bg-[#C87D46] px-6 py-3 text-sm font-medium text-white shadow-lg shadow-[#C87D46]/20 hover:bg-[#D98E58] transition-all"
                >
                  <span>Læs hele historien om HiFi-universet</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Right imagery grid - editorial composition */}
            <div className="lg:col-span-6 grid grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-4 sm:space-y-6">
                <div className="overflow-hidden rounded-sm border border-white/10 bg-[#18181C] shadow-xl">
                  <img
                    src={getOptimizedImageUrl(ampImage, { width: 800, quality: 85 })}
                    alt="Mono rørudgangstrin"
                    className="w-full aspect-[3/4] object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>

              <div className="space-y-4 sm:space-y-6 pt-8">
                <div className="overflow-hidden rounded-sm border border-white/10 bg-[#18181C] shadow-xl">
                  <img
                    src={getOptimizedImageUrl(detailImage, { width: 800, quality: 85 })}
                    alt="Håndværk og samlinger"
                    className="w-full aspect-[3/4] object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LYTTEOPLEVELSEN SECTION */}
      <section className="py-20 sm:py-28 bg-[#0E0E10] border-b border-white/10 relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start relative pb-8 lg:pb-16">
            {/* Left Column: Primary Text Hierarchy */}
            <div className="lg:col-span-5 z-10 space-y-6">
              {/* Eyebrow label inside a pill */}
              <div className="inline-flex items-center gap-2 rounded-full border border-[#C87D46]/40 bg-[#18181C] px-4 py-1.5 text-xs uppercase tracking-widest text-[#C87D46] font-medium">
                Lytteoplevelsen
              </div>

              {/* Large primary heading */}
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F5F3EF] leading-[1.15]">
                Når lytteren føler sig omslugt af musikken
              </h2>

              {/* Supporting paragraph */}
              <p className="text-sm sm:text-base text-[#A8A49C] leading-relaxed">
                Et lytterum er ikke et laboratorium, men et intimt rum dedikeret til nærvær og fordybelse. Her kan du læne dig tilbage, høre dine egne favoritplader og opleve detaljer i lydbilledet, du måske aldrig har lagt mærke til før.
              </p>

              {/* Large statistic below the heading */}
              <div className="pt-2 flex items-baseline gap-4">
                <span className="font-serif text-5xl sm:text-6xl lg:text-4xl xl:text-5xl font-bold text-[#C87D46] tracking-tight">
                  30+
                </span>
                <div className="text-xs sm:text-sm text-[#E5E2DB] leading-snug">
                  <span className="font-semibold block text-[#F5F3EF]">Års erfaring</span>
                  <span className="text-[#A8A49C]">med DIY HiFi, rørforstærkere og højtalerbyg</span>
                </div>
              </div>

              {/* CTA button */}
              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-sm bg-[#C87D46] px-6 py-3.5 text-xs sm:text-sm font-medium text-white shadow-lg shadow-[#C87D46]/25 hover:bg-[#D98E58] transition-colors"
                >
                  <span>Aftal et lyt i lytterummet</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Right Column: Prominent Image with rounded corners */}
            <div className="lg:col-span-7 relative lg:pt-2">
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl relative">
                <img
                  src={getOptimizedImageUrl(listeningPosImage, { width: 1200, quality: 85 })}
                  alt="Lyttepositionen og pladereolen"
                  className="w-full aspect-[16/11] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-4 left-4 right-4 text-xs text-[#E5E2DB]/90 hidden sm:block">
                  Lyttepositionen og pladereolen med meget forskelligt musik, samlet igennem mange år
                </div>
              </div>

              {/* Overlapping feature card floating across the boundary */}
              <div className="mt-8 lg:mt-0 lg:absolute lg:-bottom-12 lg:-left-20 lg:w-[110%] z-20 lg:translate-x-12 lg:translate-y-12">
                <div className="rounded-xl border border-[#C87D46]/40 bg-[#18181C] p-6 sm:p-7 shadow-2xl backdrop-blur-md">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
                    {/* Feature block 1 */}
                    <div className="flex items-start gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#C87D46]/15 border border-[#C87D46]/30 text-[#C87D46]">
                        <i className="fa-solid fa-record-vinyl text-lg" aria-hidden="true" />
                      </div>
                      <div>
                        <h4 className="font-serif text-base text-[#F5F3EF]">
                          Dine egne favoritplader
                        </h4>
                        <p className="mt-1 text-xs text-[#A8A49C] leading-relaxed">
                          Tag din egen vinyl eller musik med og oplev ren dynamik, nerve og varme.
                        </p>
                      </div>
                    </div>

                    {/* Feature block 2 */}
                    <div className="flex items-start gap-4 sm:pl-6 pt-4 sm:pt-0">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#C87D46]/15 border border-[#C87D46]/30 text-[#C87D46]">
                        <i className="fa-solid fa-mug-hot text-lg" aria-hidden="true" />
                      </div>
                      <div>
                        <h4 className="font-serif text-base text-[#F5F3EF]">
                          En kop frisk kaffe
                        </h4>
                        <p className="mt-1 text-xs text-[#A8A49C] leading-relaxed">
                          Uformel stemning og god tid til en personlig dialog om akustik og anlæg.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CMS-BASED HiFi OFFERINGS & SERVICES SECTION */}
      <section className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <span className="text-xs uppercase tracking-widest text-[#C87D46] font-medium block mb-2">
                Hvad vi tilbyder
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#F5F3EF]">
                Ydelser & Produkter i JARC Audio
              </h2>
            </div>
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-2 text-sm text-[#C87D46] hover:text-[#D98E58] transition-colors font-medium whitespace-nowrap"
            >
              <span>Se alle ydelser og detaljer</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {hifiItems.map((item) => {
              return (
                <div
                  key={item.id || item.slug}
                  className="rounded-sm border border-white/10 bg-[#18181C] p-8 flex flex-col justify-between hover:border-[#C87D46]/40 transition-colors group"
                >
                  <div>
                    <h3 className="font-serif text-xl text-[#F5F3EF]">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-xs sm:text-sm text-[#A8A49C] leading-relaxed">
                      {item.summary}
                    </p>
                  </div>

                  <div className="pt-6 border-t border-white/5 mt-6">
                    <Link
                      to="/kontakt"
                      className="inline-flex items-center gap-2 text-xs font-medium text-[#C87D46] hover:text-[#D98E58] transition-colors"
                    >
                      <span>Kontakt for dialog</span>
                      <i className="fa-solid fa-chevron-right text-[9px]" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* INVITATION & BESØG SECTION */}
      <section
        className="py-24 sm:py-32 relative bg-fixed bg-cover bg-center"
        style={{
          backgroundImage: `url('https://assets.auxodev.dk/assets/d6e713fb-6411-4075-ab6a-5936b7a50d5a/scandinavian-hifi-listening-room.png')`
        }}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-[2px]" />
        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-sm border border-[#C87D46]/30 bg-[#18181C]/90 p-8 sm:p-12 md:p-16 text-center relative overflow-hidden shadow-2xl backdrop-blur-sm">
            <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-[#C87D46]/5 blur-3xl pointer-events-none" />

            <span className="inline-block text-xs uppercase tracking-widest text-[#C87D46] font-medium mb-3">
              Personlig oplevelse
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#F5F3EF] leading-tight max-w-2xl mx-auto">
              “Kom forbi til et nyt lyt og en kop kaffe”
            </h2>

            <p className="mt-6 text-sm sm:text-base text-[#A8A49C] max-w-xl mx-auto leading-relaxed">
              Besøget handler ikke blot om at købe udstyr, men om at opleve musikken, udveksle erfaringer og få ærlig og personlig rådgivning.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6 text-xs sm:text-sm text-[#E5E2DB]">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-location-dot text-[#C87D46]" aria-hidden="true" />
                <span>Adresse / Lytterum: <strong>Toftehøj 21, 6470 Sydals</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-phone text-[#C87D46]" aria-hidden="true" />
                <a href="tel:+4522884536" className="whitespace-nowrap hover:text-[#C87D46] transition-colors">
                  +45 22 88 45 36
                </a>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-sm bg-[#C87D46] px-8 py-3.5 text-sm font-medium text-white shadow-xl shadow-[#C87D46]/25 hover:bg-[#D98E58] transition-all"
              >
                <i className="fa-solid fa-mug-hot text-xs" aria-hidden="true" />
                <span>Kontakt os for at aftale tid</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
